// @flow

import type {BindOptions, ElementData, ResponseDataItem, Targets, UnbindOptions} from './types'

import {ajax, onCustomEvent, type} from 'main.core'

const DEFAULT_BIND_EVENT_NAMES = ['click']
const DEFAULT_TARGET_SELECTOR = '[data-favorite],[data-favorite-item-id]'

let defaultBindOptions: BindOptions = {
    target: DEFAULT_TARGET_SELECTOR,
    eventNames: DEFAULT_BIND_EVENT_NAMES
}

let defaultUnbindOptions: UnbindOptions = {
    target: DEFAULT_TARGET_SELECTOR,
}

export const DEFAULT_TYPE = 'F'

export const EVENT_NAME_BEFORE_SEND = 'ITJUST_FAVORITE_BEFORE_SEND'
export const EVENT_NAME_SUCCESS = 'ITJUST_FAVORITE_SUCCESS'
export const EVENT_NAME_FAIL = 'ITJUST_FAVORITE_FAIL'
// export const EVENT_NAME_LOAD_LIST = 'ITJUST_FAVORITE_LOAD_LIST'

const ATTR_ITEM_ID = 'ItemId'
const ATTR_TYPE = 'Type'
const ATTR_PARAM1 = 'Param1'
const ATTR_PARAM2 = 'Param2'
const ATTR_ADDED = 'Added'

export const AJAX_ACTION_ADD = 'itjust:favorite.Item.add'
export const AJAX_ACTION_REMOVE = 'itjust:favorite.Item.remove'
export const AJAX_ACTION_CLEAN = 'itjust:favorite.Item.clean'
export const AJAX_ACTION_LOAD = 'itjust:favorite.Item.loadList'
export const AJAX_ACTION_ADD_MULTI = 'itjust:favorite.Item.addMulti'

export const ACTION_ADD = 'add'
export const ACTION_REMOVE = 'remove'
export const ACTION_LOAD = 'load'

function elementData(element: HTMLElement, name: string, def: any = undefined, set: boolean = false) {
    const v = element.dataset['favorite' + name]
    if (set) {
        element.dataset['favorite' + name] = def
        return def
    }
    return v ?? def
}

export function extractData(element: HTMLElement, forSend: boolean = false): ElementData {
    const signer = elementData(element, '')
    if (signer) {
        if (forSend){
            return {
                itemId: signer
            }
        } else {
            let str = signer.split('.')[0]
            str = atob(str)
            return JSON.parse(str)
        }
    }

    const itemId = elementData(element, ATTR_ITEM_ID),
        type = elementData(element, ATTR_TYPE),
        param1 = elementData(element, ATTR_PARAM1),
        param2 = elementData(element, ATTR_PARAM2)

    const data: ElementData = {
        itemId
    }

    if (type) {
        data.type = type
    }

    if (param1) {
        data.param1 = param1
    }
    if (param2) {
        data.param2 = param2
    }

    return data
}

function isAdded(element: HTMLElement): boolean {
    return elementData(element, ATTR_ADDED, 'false') === 'true';
}

function setAdded(element: HTMLElement, value: boolean): void {
    elementData(element, ATTR_ADDED, value, true)
}

function handler() {
    const elementData = extractData(this, true)
    let actionUrl = AJAX_ACTION_ADD
    let action = ACTION_ADD

    if (isAdded(this)) {
        actionUrl = AJAX_ACTION_REMOVE
        action = ACTION_REMOVE
    }

    onCustomEvent(EVENT_NAME_BEFORE_SEND, [this, elementData, action])

    ajax.runAction(actionUrl, {
        data: elementData
    }).then((response: any) => {
        setAdded(this, action === ACTION_ADD)
        onCustomEvent(EVENT_NAME_SUCCESS, [this, action, response, extractData(this)])
    }).catch((reason: any) => {
        onCustomEvent(EVENT_NAME_FAIL, [this, action, reason, extractData(this)])
    })
}

const bindingElements: Map<HTMLElement, Set<string>> = new Map()

function getTargets(targets: Targets): NodeListOf<HTMLElement> | HTMLElement[] {
    if (typeof targets === 'string') {
        return document.querySelectorAll(targets)
    } else if (targets instanceof HTMLElement) {
        return [targets]
    }

    return targets
}

export function bind(options: BindOptions | string = defaultBindOptions) {
    if (typeof options === 'string') {
        let tmp = options
        options = defaultBindOptions
        options.target = tmp
    }

    let targets = getTargets(options.target)

    targets.forEach((element: HTMLElement) => {
        if (bindingElements.has(element)) {
            return
        }

        let eventNames: Set<string> = bindingElements.get(element) || new Set()

        options.eventNames.forEach(eventName => {
            element.addEventListener(eventName, handler)
            eventNames.add(eventName)
        })
        bindingElements.set(element, eventNames)
    })
}

export function unbind(options: UnbindOptions | string = defaultUnbindOptions) {
    if (typeof options === 'string') {
        let tmp = options
        options = defaultUnbindOptions
        options.target = tmp
    }

    let targets = getTargets(options.target)

    targets.forEach((element: HTMLElement) => {
        if (!bindingElements.has(element)) {
            return
        }

        let eventNames: Set<string> = bindingElements.get(element) || new Set()

        eventNames.forEach((eventName: string) => element.removeEventListener(eventName, handler))
        bindingElements.delete(element)
    })
}


function inFavorite(element: HTMLElement, data: ResponseDataItem): boolean {
    const elData = extractData(element)

    return elData.itemId === data.ITEM_ID
        && ((elData.type ?? DEFAULT_TYPE) === data.TYPE)
}

export function load() {
    ajax.runAction(AJAX_ACTION_LOAD).then(response => {
        const items: Array<ResponseDataItem> = response.data

        if (type.isArray(items)) {
            bindingElements.forEach((_, element) => {
                let ok = false
                let elementData = undefined
                if (items.length > 0) {
                    for (const i in items) {
                        if (inFavorite(element, items[i])) {
                            ok = true
                            elementData = items[i]
                            delete items[i]
                            break
                        }
                    }
                }
                setAdded(element, ok)
                onCustomEvent(EVENT_NAME_SUCCESS, [element, ACTION_LOAD, ok, elementData])
            })
        } else {
            throw new Error('Пришел не массив')
        }
    }).catch(console.warn)
}

export function clean(type?: string) {
    const data: { type?: string } = {}
    if (type) {
        data.type = type
    }
    return ajax.runAction(AJAX_ACTION_CLEAN, {
        data: data
    })
}